import RAPIER from "@dimforge/rapier3d-compat";
import * as THREE from "three";
import { CollisionLayer, PLAYER_GROUPS, interactionGroups } from "../physics/layers";
import type { PlayerController } from "../player/PlayerController";
import type { VehicleRoute } from "../world/RoadNetwork";
import {
  VehicleModelFactory,
  type CivilianVehicleType,
  type VehicleDefinition,
  type VehicleModelInstance,
  type VehicleType,
} from "./vehicleModels";
import { disposeVehicleTextures, loadVehicleTextures } from "./vehicleTextures";

export type CarState = "traffic" | "driving" | "abandoned" | "disabled";

export interface CarSnapshot {
  id: number;
  x: number;
  z: number;
  health: number;
  state: CarState;
  type: VehicleType;
  color: number;
  wheelRotation: number;
  lightsOn: boolean;
  routeId: string;
  laneId: string | null;
  headingDegrees: number;
}

export interface DrivingSnapshot {
  carId: number;
  speedKph: number;
  health: number;
}

export interface VehicleEventSnapshot {
  sequence: number;
  damage: number;
  drivenHealth: number;
  otherCarId: number;
  otherHealth: number;
  drivenDisabled: boolean;
  otherDisabled: boolean;
  exitBlocked: boolean;
}

interface CarSpawnBase {
  route: VehicleRoute;
  progress: number;
  speedMps: number;
  health?: number;
}

export type CarSpawn =
  | (CarSpawnBase & { type: CivilianVehicleType; color: number })
  | (CarSpawnBase & { type: "taxi" | "police"; color?: never });

export interface VehicleSnapshot {
  cars: CarSnapshot[];
  driving: DrivingSnapshot | null;
  vehicleEvent: VehicleEventSnapshot | null;
  activeHeadlightSources: number;
  shadowCastingHeadlightSources: number;
}

export const MAX_LOCAL_HEADLIGHT_VEHICLES = 2 as const;
const HEADLIGHT_SELECTION_RADIUS_M = 120;
const HEADLIGHT_RANGE_M = 70;
const HEADLIGHT_REFRESH_DISTANCE_M = 4;
const HEADLIGHT_REFRESH_SECONDS = 0.25;

interface CarRecord {
  id: number;
  type: VehicleType;
  model: VehicleModelInstance;
  definition: VehicleDefinition;
  colliderCenterY: number;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  route: VehicleRoute;
  routeDistanceM: number;
  routeSegmentIndex: number;
  speedMps: number;
  health: number;
  state: CarState;
  preStepVelocity: THREE.Vector2;
  previousPosition: THREE.Vector3;
  currentPosition: THREE.Vector3;
  previousRotation: THREE.Quaternion;
  currentRotation: THREE.Quaternion;
  previousWheelRotation: number;
  currentWheelRotation: number;
}

const VEHICLE_GROUPS = interactionGroups(
  CollisionLayer.VEHICLE,
  CollisionLayer.WORLD | CollisionLayer.VEHICLE | CollisionLayer.PLAYER,
);
const EGRESS_OFFSETS = [
  new THREE.Vector2(0, 3.1),
  new THREE.Vector2(0, -3.1),
  new THREE.Vector2(-3.4, 2.5),
  new THREE.Vector2(-3.4, -2.5),
  new THREE.Vector2(-5, 0),
] as const;
const DAMAGED_PAINT_COLOR = new THREE.Color(0x25292a);

export function damageFromImpact(relativeSpeedMps: number): number {
  return THREE.MathUtils.clamp(Math.round((relativeSpeedMps - 3) * 4), 0, 50);
}

export function applyDamage(health: number, damage: number): { health: number; disabled: boolean } {
  const nextHealth = THREE.MathUtils.clamp(Math.round(health - damage), 0, 100);
  return { health: nextHealth, disabled: nextHealth === 0 };
}

export class VehicleSystem {
  readonly eventQueue = new RAPIER.EventQueue(true);
  readonly #cars = new Map<number, CarRecord>();
  readonly #carsByCollider = new Map<number, CarRecord>();
  readonly #pairCooldowns = new Map<string, number>();
  readonly #ambientCars = new Map<number, readonly string[]>();
  #nextId = 1;
  #nextAmbientSequence = 0;
  #controlled?: CarRecord;
  #vehicleEvent: VehicleEventSnapshot | null = null;
  #eventSequence = 0;
  #elapsedSeconds = 0;
  readonly #cameraFocus = new THREE.Vector3();
  readonly #cameraFocusTarget = new THREE.Vector3();
  readonly #wheelForward = new THREE.Vector3();
  readonly #wheelQuaternion = new THREE.Quaternion();
  #cameraInitialized = false;
  readonly #driveQuaternion = new THREE.Quaternion();
  readonly #driveForward = new THREE.Vector3();
  readonly #driveLateral = new THREE.Vector3();
  readonly #seatedCameraEuler = new THREE.Euler(0, 0, 0, "YXZ");
  readonly #seatedCameraOffset = new THREE.Vector3();
  readonly #desiredCameraPosition = new THREE.Vector3();
  readonly #cameraDirection = new THREE.Vector3();
  readonly #cameraRay = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: -1 });
  #modelFactory?: VehicleModelFactory;
  #modelLoad?: Promise<void>;
  #lightsEnabled = false;
  readonly #headlights: THREE.SpotLight[];
  readonly #headlightVehicles: CarRecord[] = [];
  readonly #headlightDistances = new Float64Array(MAX_LOCAL_HEADLIGHT_VEHICLES);
  readonly #headlightSource = new THREE.Vector3();
  readonly #headlightTarget = new THREE.Vector3();
  #shadowBudget = 0;
  readonly #lastHeadlightCameraPosition = new THREE.Vector3(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
  );
  #lastHeadlightSelectionSeconds = Number.NEGATIVE_INFINITY;
  #headlightSelectionDirty = true;
  #headlightControlled?: CarRecord;
  #activeHeadlightSources = 0;
  #disposed = false;

  constructor(
    readonly world: RAPIER.World,
    readonly scene: THREE.Scene,
    readonly camera: THREE.PerspectiveCamera,
    readonly player: PlayerController,
  ) {
    this.#headlights = Array.from(
      { length: MAX_LOCAL_HEADLIGHT_VEHICLES * 2 },
      (_, sourceIndex) => {
        const vehicleIndex = Math.floor(sourceIndex / 2);
        const side = sourceIndex % 2 === 0 ? "left" : "right";
        const light = new THREE.SpotLight(
          0xffe3a3,
          0,
          HEADLIGHT_RANGE_M,
          THREE.MathUtils.degToRad(14),
          0.45,
          2,
        );
        light.name = `Vehicle headlight ${vehicleIndex + 1} ${side}`;
        light.target.name = `${light.name} target`;
        light.castShadow = false;
        light.shadow.mapSize.set(512, 512);
        light.shadow.camera.near = 0.5;
        light.shadow.camera.far = HEADLIGHT_RANGE_M;
        light.shadow.bias = -0.0005;
        light.shadow.normalBias = 0.04;
        light.visible = false;
        scene.add(light, light.target);
        return light;
      },
    );
  }

  async loadModels(maxAnisotropy: number): Promise<void> {
    if (this.#disposed) throw new Error("Vehicle system is disposed");
    if (this.#modelFactory) return;
    if (this.#modelLoad) {
      await this.#modelLoad;
      return;
    }
    const pending = loadVehicleTextures(maxAnisotropy).then((textures) => {
      if (this.#disposed) {
        disposeVehicleTextures(textures);
        throw new Error("Vehicle system is disposed");
      }
      const factory = new VehicleModelFactory(textures);
      factory.setLightsEnabled(this.#lightsEnabled);
      this.#modelFactory = factory;
    });
    this.#modelLoad = pending;
    try {
      await pending;
    } finally {
      if (this.#modelLoad === pending) this.#modelLoad = undefined;
    }
  }

  spawnCar(spec: CarSpawn): number {
    const factory = this.#modelFactory;
    if (!factory) throw new Error("Vehicle models are not loaded");
    const id = this.#nextId++;
    const route = spec.route;
    const progress = ((spec.progress % 1) + 1) % 1;
    const routeDistanceM = progress * route.lengthM;
    let routeSegmentIndex = 1;
    while (
      routeSegmentIndex < route.cumulativeLengths.length - 1 &&
      (route.cumulativeLengths[routeSegmentIndex] ?? 0) < routeDistanceM
    )
      routeSegmentIndex += 1;
    const previousOffset = (routeSegmentIndex - 1) * 2;
    const nextOffset = routeSegmentIndex * 2;
    const segmentStartDistance = route.cumulativeLengths[routeSegmentIndex - 1] ?? 0;
    const segmentLength =
      (route.cumulativeLengths[routeSegmentIndex] ?? route.lengthM) - segmentStartDistance;
    const segmentProgress =
      segmentLength > 0 ? (routeDistanceM - segmentStartDistance) / segmentLength : 0;
    const fromX = route.points[previousOffset] ?? 0;
    const fromZ = route.points[previousOffset + 1] ?? 0;
    const toX = route.points[nextOffset] ?? fromX;
    const toZ = route.points[nextOffset + 1] ?? fromZ;
    const rotation = -Math.atan2(toZ - fromZ, toX - fromX);
    const model =
      spec.type === "taxi" || spec.type === "police"
        ? factory.create(spec.type)
        : factory.create(spec.type, spec.color);
    const { group, definition } = model;
    group.position.set(
      THREE.MathUtils.lerp(fromX, toX, segmentProgress),
      0,
      THREE.MathUtils.lerp(fromZ, toZ, segmentProgress),
    );
    group.rotation.y = rotation;
    this.scene.add(group);

    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(
          group.position.x,
          group.position.y + definition.colliderCenterY,
          group.position.z,
        )
        .setRotation({
          x: 0,
          y: Math.sin(rotation / 2),
          z: 0,
          w: Math.cos(rotation / 2),
        }),
    );
    const [halfX, halfY, halfZ] = definition.colliderHalfExtents;
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(halfX, halfY, halfZ)
        .setCollisionGroups(VEHICLE_GROUPS)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      body,
    );
    const health = THREE.MathUtils.clamp(Math.round(spec.health ?? 100), 0, 100);
    const translation = body.translation();
    const bodyRotation = body.rotation();
    const record: CarRecord = {
      id,
      type: spec.type,
      model,
      definition,
      colliderCenterY: definition.colliderCenterY,
      body,
      collider,
      route,
      routeDistanceM,
      routeSegmentIndex,
      speedMps: spec.speedMps,
      health,
      state: health === 0 ? "disabled" : "traffic",
      preStepVelocity: new THREE.Vector2(),
      previousPosition: new THREE.Vector3(translation.x, translation.y, translation.z),
      currentPosition: new THREE.Vector3(translation.x, translation.y, translation.z),
      previousRotation: new THREE.Quaternion(
        bodyRotation.x,
        bodyRotation.y,
        bodyRotation.z,
        bodyRotation.w,
      ),
      currentRotation: new THREE.Quaternion(
        bodyRotation.x,
        bodyRotation.y,
        bodyRotation.z,
        bodyRotation.w,
      ),
      previousWheelRotation: 0,
      currentWheelRotation: 0,
    };
    this.#cars.set(id, record);
    this.#carsByCollider.set(collider.handle, record);
    this.#applyVisualDamage(record);
    this.#headlightSelectionDirty = true;
    return id;
  }

  reconcileAmbientTraffic(
    center: Readonly<{ x: number; z: number }>,
    retainRadiusM: number,
    targetCount: number,
    activeTileIds: ReadonlySet<string>,
    spawn: (sequence: number) => { tileIds: readonly string[]; spec: CarSpawn } | undefined,
  ): void {
    const removable: Array<{ car: CarRecord; distance: number }> = [];
    for (const [id, tileIds] of this.#ambientCars) {
      const car = this.#cars.get(id);
      if (!car) {
        this.#ambientCars.delete(id);
        continue;
      }
      const position = car.body.translation();
      const distance = Math.hypot(position.x - center.x, position.z - center.z);
      if (
        car !== this.#controlled &&
        (tileIds.some((tileId) => !activeTileIds.has(tileId)) || distance > retainRadiusM)
      )
        removable.push({ car, distance: Number.POSITIVE_INFINITY });
      else if (car !== this.#controlled) removable.push({ car, distance });
    }
    for (const { car, distance } of removable) {
      if (distance === Number.POSITIVE_INFINITY) this.#removeCar(car);
    }

    if (this.#ambientCars.size > targetCount) {
      const excess = [...this.#ambientCars.keys()]
        .map((id) => this.#cars.get(id))
        .filter((car): car is CarRecord => car !== undefined && car !== this.#controlled)
        .sort((left, right) => {
          const leftPosition = left.body.translation();
          const rightPosition = right.body.translation();
          return (
            Math.hypot(rightPosition.x - center.x, rightPosition.z - center.z) -
            Math.hypot(leftPosition.x - center.x, leftPosition.z - center.z)
          );
        })
        .slice(0, this.#ambientCars.size - targetCount);
      for (const car of excess) this.#removeCar(car);
    }

    while (this.#ambientCars.size < targetCount) {
      const sequence = this.#nextAmbientSequence;
      const candidate = spawn(sequence);
      if (!candidate) break;
      this.#nextAmbientSequence += 1;
      const id = this.spawnCar(candidate.spec);
      this.#ambientCars.set(id, candidate.tileIds);
    }
  }

  nearestHijackable(position: { x: number; z: number }, maxDistance: number): number | null {
    let nearest: CarRecord | undefined;
    let nearestDistance = maxDistance;
    for (const car of this.#cars.values()) {
      if ((car.state !== "traffic" && car.state !== "abandoned") || car.health === 0) continue;
      const translation = car.body.translation();
      const distance = Math.hypot(position.x - translation.x, position.z - translation.z);
      if (
        distance < nearestDistance ||
        (distance === nearestDistance && nearest !== undefined && car.id < nearest.id)
      ) {
        nearest = car;
        nearestDistance = distance;
      }
    }
    return nearest?.id ?? null;
  }

  enter(carId: number, collideWithVehicles = true): boolean {
    if (this.#controlled) return false;
    const car = this.#cars.get(carId);
    if (!car || (car.state !== "traffic" && car.state !== "abandoned") || car.health === 0)
      return false;
    const translation = { ...car.body.translation() };
    const rotation = { ...car.body.rotation() };
    car.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
    car.collider.setMass(car.definition.mass);
    car.body.recomputeMassPropertiesFromColliders();
    car.collider.setFriction(0.9);
    car.collider.setCollisionGroups(
      collideWithVehicles
        ? VEHICLE_GROUPS
        : interactionGroups(CollisionLayer.VEHICLE, CollisionLayer.WORLD),
    );
    car.collider.setRestitution(0.08);
    car.body.enableCcd(true);
    car.body.setLinearDamping(collideWithVehicles ? 0.4 : 4);
    car.body.setGravityScale(collideWithVehicles ? 1 : 0, true);
    car.body.setAngularDamping(4);
    car.body.setTranslation(translation, true);
    car.body.setRotation(rotation, true);
    car.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    car.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    car.state = "driving";
    this.#controlled = car;
    this.#headlightSelectionDirty = true;
    this.#cameraInitialized = false;
    this.player.yaw = car.model.group.rotation.y - Math.PI / 2;
    this.player.root.visible = false;
    this.player.collider.setCollisionGroups(interactionGroups(CollisionLayer.PLAYER, 0));
    this.player.collider.setEnabled(false);
    this.player.setControlMode("seated");
    return true;
  }

  exit(): "exited" | "blocked" | "none" {
    const car = this.#controlled;
    if (!car) return "none";
    const velocity = car.body.linvel();
    car.body.setLinvel({ x: 0, y: velocity.y, z: 0 }, true);
    car.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    const exited = this.#tryEgress(car);
    if (!exited) {
      this.#recordExitBlocked();
      return "blocked";
    }
    car.body.setGravityScale(1, true);
    if (car.health > 0) car.state = "abandoned";
    return "exited";
  }

  snapshot(): VehicleSnapshot {
    const cars = [...this.#cars.values()]
      .sort((left, right) => left.id - right.id)
      .map((car) => {
        const position = car.body.translation();
        return {
          id: car.id,
          x: position.x,
          z: position.z,
          health: car.health,
          state: car.state,
          type: car.type,
          color: car.model.resolvedColor,
          wheelRotation: car.currentWheelRotation,
          lightsOn: this.#lightsEnabled,
          routeId: car.route.id,
          laneId:
            car.route.laneIds[Math.min(car.routeSegmentIndex - 1, car.route.laneIds.length - 1)] ??
            null,
          headingDegrees: (() => {
            const offset = car.routeSegmentIndex * 2;
            const deltaX = (car.route.points[offset] ?? 0) - (car.route.points[offset - 2] ?? 0);
            const deltaZ =
              (car.route.points[offset + 1] ?? 0) - (car.route.points[offset - 1] ?? 0);
            return (Math.atan2(deltaX, -deltaZ) * (180 / Math.PI) + 360) % 360;
          })(),
        };
      });
    const driving = this.#controlled
      ? {
          carId: this.#controlled.id,
          speedKph: Math.round(this.#horizontalSpeed(this.#controlled.body.linvel()) * 3.6),
          health: this.#controlled.health,
        }
      : null;
    const activeHeadlightSources = this.#activeHeadlightSources;
    const shadowCastingHeadlightSources = Math.min(activeHeadlightSources, this.#shadowBudget * 2);
    return {
      cars,
      driving,
      vehicleEvent: this.#vehicleEvent,
      activeHeadlightSources,
      shadowCastingHeadlightSources,
    };
  }

  setShadowBudget(vehicleCount: number): void {
    const budget = THREE.MathUtils.clamp(Math.trunc(vehicleCount), 0, MAX_LOCAL_HEADLIGHT_VEHICLES);
    for (let sourceIndex = 0; sourceIndex < this.#headlights.length; sourceIndex += 1) {
      const light = this.#headlights[sourceIndex]!;
      const enabled = Math.floor(sourceIndex / 2) < budget;
      if (enabled && !light.castShadow) light.shadow.needsUpdate = true;
      light.castShadow = enabled;
    }
    this.#shadowBudget = budget;
  }

  setLightsEnabled(enabled: boolean): void {
    if (enabled === this.#lightsEnabled) return;
    this.#lightsEnabled = enabled;
    this.#modelFactory?.setLightsEnabled(enabled);
    this.#headlightSelectionDirty = true;
    if (!enabled) this.#hideHeadlights();
  }

  fixedUpdate(stepSeconds: number): void {
    this.#elapsedSeconds += stepSeconds;
    for (const car of this.#cars.values()) {
      if (car.state === "traffic") this.#advanceTraffic(car, stepSeconds);
    }
    for (const car of this.#cars.values()) {
      if (car.state !== "traffic") this.#keepUpright(car);
    }
    if (this.#controlled && this.#controlled.health > 0) this.#drive(this.#controlled, stepSeconds);
    for (const car of this.#cars.values()) {
      this.#advanceWheels(car, stepSeconds);
      this.#storePreStepVelocity(car);
    }
  }

  afterPhysics(): void {
    this.eventQueue.drainCollisionEvents((handle1, handle2, started) => {
      if (!started || !this.#controlled) return;
      const first = this.#carsByCollider.get(handle1);
      const second = this.#carsByCollider.get(handle2);
      if (!first || !second || first === second) return;
      const driven = this.#controlled;
      if (first !== driven && second !== driven) return;
      const other = first === driven ? second : first;
      const pairKey = `${Math.min(driven.id, other.id)}:${Math.max(driven.id, other.id)}`;
      if ((this.#pairCooldowns.get(pairKey) ?? -Infinity) > this.#elapsedSeconds) return;
      const damage = damageFromImpact(driven.preStepVelocity.distanceTo(other.preStepVelocity));
      if (damage === 0) return;
      this.#pairCooldowns.set(pairKey, this.#elapsedSeconds + 0.5);
      const drivenResult = applyDamage(driven.health, damage);
      const otherResult = applyDamage(other.health, damage);
      driven.health = drivenResult.health;
      other.health = otherResult.health;
      if (otherResult.disabled) this.#disable(other);
      if (drivenResult.disabled) this.#disable(driven);
      let exitBlocked = false;
      if (drivenResult.disabled && !this.#tryEgress(driven)) exitBlocked = true;
      this.#vehicleEvent = {
        sequence: ++this.#eventSequence,
        damage,
        drivenHealth: driven.health,
        otherCarId: other.id,
        otherHealth: other.health,
        drivenDisabled: drivenResult.disabled,
        otherDisabled: otherResult.disabled,
        exitBlocked,
      };
    });
    for (const car of this.#cars.values()) {
      if (car.state !== "traffic") this.#keepUpright(car);
    }
    for (const car of this.#cars.values()) {
      const translation = car.body.translation();
      const rotation = car.body.rotation();
      car.previousPosition.copy(car.currentPosition);
      car.currentPosition.set(translation.x, translation.y, translation.z);
      car.previousRotation.copy(car.currentRotation);
      car.currentRotation.set(rotation.x, rotation.y, rotation.z, rotation.w);
    }
  }

  frameUpdate(alpha = 1): void {
    const interpolation = THREE.MathUtils.clamp(alpha, 0, 1);
    for (const car of this.#cars.values()) {
      const group = car.model.group;
      group.position.lerpVectors(car.previousPosition, car.currentPosition, interpolation);
      group.position.y -= car.colliderCenterY;
      group.quaternion.slerpQuaternions(car.previousRotation, car.currentRotation, interpolation);
      const wheelRotation = THREE.MathUtils.lerp(
        car.previousWheelRotation,
        car.currentWheelRotation,
        interpolation,
      );
      for (const pivot of car.model.wheelPivots) pivot.rotation.z = wheelRotation;
    }
    this.#updateHeadlights();
    const car = this.#controlled;
    if (!car) return;
    const translation = car.body.translation();
    this.player.body.setTranslation(
      { x: translation.x, y: translation.y + 0.025, z: translation.z },
      true,
    );
    const group = car.model.group;
    const focusTarget = this.#cameraFocusTarget.set(
      group.position.x,
      group.position.y + car.definition.cameraFocusY,
      group.position.z,
    );
    if (!this.#cameraInitialized) {
      this.#cameraFocus.copy(focusTarget);
      this.#cameraInitialized = true;
    } else {
      this.#cameraFocus.lerp(focusTarget, 0.18);
    }
    const focus = this.#cameraFocus;
    this.#seatedCameraEuler.set(this.player.pitch, this.player.yaw, 0, "YXZ");
    this.#seatedCameraOffset.set(0, 3.6, 10).applyEuler(this.#seatedCameraEuler);
    this.#desiredCameraPosition.copy(focus).add(this.#seatedCameraOffset);
    this.#cameraDirection.copy(this.#desiredCameraPosition).sub(focus);
    const directionLength = this.#cameraDirection.length();
    this.#cameraRay.origin.x = focus.x;
    this.#cameraRay.origin.y = focus.y;
    this.#cameraRay.origin.z = focus.z;
    this.#cameraDirection.normalize();
    this.#cameraRay.dir.x = this.#cameraDirection.x;
    this.#cameraRay.dir.y = this.#cameraDirection.y;
    this.#cameraRay.dir.z = this.#cameraDirection.z;
    const hit = this.world.castRay(
      this.#cameraRay,
      directionLength,
      true,
      undefined,
      undefined,
      car.collider,
    );
    if (hit)
      this.#desiredCameraPosition
        .copy(focus)
        .add(this.#cameraDirection.multiplyScalar(Math.max(5.5, hit.timeOfImpact - 0.3)));
    this.camera.position.lerp(this.#desiredCameraPosition, 0.18);
    this.camera.lookAt(focus);
  }

  #updateHeadlights(): void {
    if (!this.#lightsEnabled) {
      this.#hideHeadlights();
      return;
    }
    const radiusSquared = HEADLIGHT_SELECTION_RADIUS_M * HEADLIGHT_SELECTION_RADIUS_M;
    let selectedOutsideRadius = false;
    for (const car of this.#headlightVehicles) {
      if (car === this.#controlled) continue;
      const group = car.model.group;
      const deltaX = group.position.x - this.camera.position.x;
      const deltaY = group.position.y - this.camera.position.y;
      const deltaZ = group.position.z - this.camera.position.z;
      if (deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ > radiusSquared) {
        selectedOutsideRadius = true;
        break;
      }
    }
    const movedEnough =
      this.#lastHeadlightCameraPosition.distanceToSquared(this.camera.position) >=
      HEADLIGHT_REFRESH_DISTANCE_M * HEADLIGHT_REFRESH_DISTANCE_M;
    const deadlineReached =
      this.#elapsedSeconds - this.#lastHeadlightSelectionSeconds >= HEADLIGHT_REFRESH_SECONDS;
    if (
      this.#headlightSelectionDirty ||
      movedEnough ||
      deadlineReached ||
      selectedOutsideRadius ||
      this.#headlightControlled !== this.#controlled
    )
      this.#selectHeadlightVehicles(radiusSquared);
    this.#activeHeadlightSources = this.#headlightVehicles.length * 2;
    for (let vehicleIndex = 0; vehicleIndex < MAX_LOCAL_HEADLIGHT_VEHICLES; vehicleIndex += 1) {
      const car = this.#headlightVehicles[vehicleIndex];
      for (let sideIndex = 0; sideIndex < 2; sideIndex += 1) {
        const light = this.#headlights[vehicleIndex * 2 + sideIndex]!;
        light.visible = Boolean(car);
        light.intensity = car ? 900 : 0;
        if (!car) continue;
        const side = sideIndex === 0 ? -1 : 1;
        const definition = car.definition;
        const lateralOffset = side * definition.width * 0.29;
        this.#headlightSource
          .set(definition.length * 0.505, definition.height * 0.39, lateralOffset)
          .applyQuaternion(car.model.group.quaternion)
          .add(car.model.group.position);
        this.#headlightTarget
          .set(definition.length * 0.505 + 45, 0.2, lateralOffset)
          .applyQuaternion(car.model.group.quaternion)
          .add(car.model.group.position);
        light.position.copy(this.#headlightSource);
        light.target.position.copy(this.#headlightTarget);
      }
    }
  }

  #selectHeadlightVehicles(radiusSquared: number): void {
    const reserved = this.#controlled ? 1 : 0;
    let selectedCount = reserved;
    this.#headlightVehicles.length = selectedCount;
    if (this.#controlled) {
      this.#headlightVehicles[0] = this.#controlled;
      this.#headlightDistances[0] = Number.NEGATIVE_INFINITY;
    }
    for (const car of this.#cars.values()) {
      if (car === this.#controlled) continue;
      const group = car.model.group;
      const deltaX = group.position.x - this.camera.position.x;
      const deltaY = group.position.y - this.camera.position.y;
      const deltaZ = group.position.z - this.camera.position.z;
      const distanceSquared = deltaX * deltaX + deltaY * deltaY + deltaZ * deltaZ;
      if (distanceSquared > radiusSquared) continue;
      let insertion = selectedCount;
      while (insertion > reserved && this.#headlightDistances[insertion - 1]! > distanceSquared)
        insertion -= 1;
      if (insertion >= MAX_LOCAL_HEADLIGHT_VEHICLES) continue;
      for (
        let index = Math.min(selectedCount, MAX_LOCAL_HEADLIGHT_VEHICLES - 1);
        index > insertion;
        index -= 1
      ) {
        this.#headlightVehicles[index] = this.#headlightVehicles[index - 1]!;
        this.#headlightDistances[index] = this.#headlightDistances[index - 1]!;
      }
      this.#headlightVehicles[insertion] = car;
      this.#headlightDistances[insertion] = distanceSquared;
      if (selectedCount < MAX_LOCAL_HEADLIGHT_VEHICLES) selectedCount += 1;
    }
    this.#headlightVehicles.length = selectedCount;
    this.#headlightControlled = this.#controlled;
    this.#lastHeadlightCameraPosition.copy(this.camera.position);
    this.#lastHeadlightSelectionSeconds = this.#elapsedSeconds;
    this.#headlightSelectionDirty = false;
  }

  #hideHeadlights(): void {
    this.#activeHeadlightSources = 0;
    this.#headlightVehicles.length = 0;
    this.#headlightControlled = undefined;
    for (const light of this.#headlights) {
      light.visible = false;
      light.intensity = 0;
    }
  }

  #removeCar(car: CarRecord): void {
    if (this.#headlightVehicles.includes(car)) this.#headlightSelectionDirty = true;
    const colliderHandle = car.collider.handle;
    this.scene.remove(car.model.group);
    this.world.removeRigidBody(car.body);
    this.#cars.delete(car.id);
    this.#carsByCollider.delete(colliderHandle);
    this.#ambientCars.delete(car.id);
    const prefix = `${car.id}:`;
    const suffix = `:${car.id}`;
    for (const key of this.#pairCooldowns.keys()) {
      if (key.startsWith(prefix) || key.endsWith(suffix)) this.#pairCooldowns.delete(key);
    }
    car.model.paintMaterial.dispose();
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#hideHeadlights();
    for (const light of this.#headlights) {
      light.removeFromParent();
      light.target.removeFromParent();
      light.shadow.dispose();
    }
    this.eventQueue.free();
    for (const car of this.#cars.values()) this.#removeCar(car);
    this.#ambientCars.clear();
    this.#pairCooldowns.clear();
    this.#modelFactory?.dispose();
    this.#modelFactory = undefined;
  }

  #advanceWheels(car: CarRecord, stepSeconds: number): void {
    car.previousWheelRotation = car.currentWheelRotation;
    let forwardSpeedMps = 0;
    if (car.state === "traffic") {
      forwardSpeedMps = car.speedMps;
    } else if (car.state === "driving" || car.state === "abandoned") {
      const rotation = car.body.rotation();
      this.#wheelQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
      this.#wheelForward.set(1, 0, 0).applyQuaternion(this.#wheelQuaternion);
      const velocity = car.body.linvel();
      forwardSpeedMps = velocity.x * this.#wheelForward.x + velocity.z * this.#wheelForward.z;
    }
    car.currentWheelRotation -= (forwardSpeedMps * stepSeconds) / car.definition.wheelRadius;
  }

  #advanceTraffic(car: CarRecord, stepSeconds: number): void {
    const route = car.route;
    if (route.lengthM < 0.5 || car.speedMps === 0) return;
    car.routeDistanceM = (car.routeDistanceM + car.speedMps * stepSeconds) % route.lengthM;
    let segmentIndex = car.routeSegmentIndex;
    while (
      segmentIndex < route.cumulativeLengths.length - 1 &&
      (route.cumulativeLengths[segmentIndex] ?? 0) < car.routeDistanceM
    )
      segmentIndex += 1;
    while (
      segmentIndex > 1 &&
      (route.cumulativeLengths[segmentIndex - 1] ?? 0) > car.routeDistanceM
    )
      segmentIndex -= 1;
    car.routeSegmentIndex = segmentIndex;
    const previousOffset = (segmentIndex - 1) * 2;
    const nextOffset = segmentIndex * 2;
    const fromX = route.points[previousOffset] ?? 0;
    const fromZ = route.points[previousOffset + 1] ?? 0;
    const toX = route.points[nextOffset] ?? fromX;
    const toZ = route.points[nextOffset + 1] ?? fromZ;
    const startDistance = route.cumulativeLengths[segmentIndex - 1] ?? 0;
    const segmentLength = (route.cumulativeLengths[segmentIndex] ?? route.lengthM) - startDistance;
    const progress = segmentLength > 0 ? (car.routeDistanceM - startDistance) / segmentLength : 0;
    const rotation = -Math.atan2(toZ - fromZ, toX - fromX);
    car.body.setNextKinematicTranslation({
      x: THREE.MathUtils.lerp(fromX, toX, progress),
      y: car.body.translation().y,
      z: THREE.MathUtils.lerp(fromZ, toZ, progress),
    });
    car.body.setNextKinematicRotation({
      x: 0,
      y: Math.sin(rotation / 2),
      z: 0,
      w: Math.cos(rotation / 2),
    });
  }

  #drive(car: CarRecord, stepSeconds: number): void {
    const rotation = car.body.rotation();
    this.#driveQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
    const forward = this.#driveForward
      .set(1, 0, 0)
      .applyQuaternion(this.#driveQuaternion)
      .normalize();
    const lateral = this.#driveLateral.set(-forward.z, 0, forward.x);
    const velocity = car.body.linvel();
    let forwardSpeed = velocity.x * forward.x + velocity.z * forward.z;
    let lateralSpeed = velocity.x * lateral.x + velocity.z * lateral.z;
    const throttle = this.player.forwardAxis;
    if (throttle > 0) forwardSpeed += 10 * stepSeconds;
    else if (throttle < 0) forwardSpeed += (forwardSpeed > 0.5 ? -18 : -7) * stepSeconds;
    else {
      const decay = 2.5 * stepSeconds;
      forwardSpeed =
        Math.abs(forwardSpeed) <= decay ? 0 : forwardSpeed - Math.sign(forwardSpeed) * decay;
    }
    forwardSpeed = THREE.MathUtils.clamp(forwardSpeed, -8, 24);
    lateralSpeed *= Math.exp(-9 * stepSeconds);
    car.body.setLinvel(
      {
        x: forward.x * forwardSpeed + lateral.x * lateralSpeed,
        y: velocity.y,
        z: forward.z * forwardSpeed + lateral.z * lateralSpeed,
      },
      true,
    );
    if (throttle !== 0) {
      const tractionImpulse = Math.sign(throttle) * 20 * car.definition.mass * stepSeconds;
      car.body.applyImpulse(
        { x: forward.x * tractionImpulse, y: 0, z: forward.z * tractionImpulse },
        true,
      );
    }
    const speedRatio = Math.min(Math.abs(forwardSpeed) / 24, 1);
    const steering =
      -this.player.turnAxis *
      Math.sign(forwardSpeed) *
      Math.min(Math.abs(forwardSpeed) / 4, 1) *
      (2 - 0.8 * speedRatio);
    car.body.setAngvel({ x: 0, y: steering, z: 0 }, true);
  }

  #keepUpright(car: CarRecord): void {
    const rotation = car.body.rotation();
    this.#driveQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
    const forward = this.#driveForward.set(1, 0, 0).applyQuaternion(this.#driveQuaternion);
    const yaw =
      Math.hypot(forward.x, forward.z) > 0.1
        ? -Math.atan2(forward.z, forward.x)
        : car.model.group.rotation.y;
    car.body.setRotation({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) }, true);
    const angularVelocity = car.body.angvel();
    car.body.setAngvel({ x: 0, y: angularVelocity.y, z: 0 }, true);
  }

  #storePreStepVelocity(car: CarRecord): void {
    if (car.state === "traffic") {
      const offset = car.routeSegmentIndex * 2;
      const fromX = car.route.points[offset - 2] ?? 0;
      const fromZ = car.route.points[offset - 1] ?? 0;
      const toX = car.route.points[offset] ?? fromX;
      const toZ = car.route.points[offset + 1] ?? fromZ;
      const length = Math.hypot(toX - fromX, toZ - fromZ);
      car.preStepVelocity.set(
        length > 0 ? ((toX - fromX) / length) * car.speedMps : 0,
        length > 0 ? ((toZ - fromZ) / length) * car.speedMps : 0,
      );
      return;
    }
    const velocity = car.body.linvel();
    car.preStepVelocity.set(velocity.x, velocity.z);
  }

  #disable(car: CarRecord): void {
    car.state = "disabled";
    car.speedMps = 0;
    car.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    car.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.#applyVisualDamage(car);
  }

  #applyVisualDamage(car: CarRecord): void {
    const ratio = 1 - car.health / 100;
    car.model.paintMaterial.color.setHex(car.model.resolvedColor).lerp(DAMAGED_PAINT_COLOR, ratio);
    car.model.damagePart.rotation.z = car.health === 0 ? 0.12 : 0;
  }

  #tryEgress(car: CarRecord): boolean {
    const translation = car.body.translation();
    const rotation = car.body.rotation();
    const quaternion = new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w);
    const capsule = new RAPIER.Capsule(0.5, 0.4);
    for (const offset of EGRESS_OFFSETS) {
      const worldOffset = new THREE.Vector3(offset.x, 0, offset.y).applyQuaternion(quaternion);
      const groundY = translation.y - car.colliderCenterY;
      const position = {
        x: translation.x + worldOffset.x,
        y: groundY + 1.1,
        z: translation.z + worldOffset.z,
      };
      const intersection = this.world.intersectionWithShape(
        position,
        { x: 0, y: 0, z: 0, w: 1 },
        capsule,
        undefined,
        PLAYER_GROUPS,
        this.player.collider,
        undefined,
        (collider) => collider.handle !== car.collider.handle,
      );
      if (intersection) continue;
      this.#controlled = undefined;
      this.#headlightSelectionDirty = true;
      this.#cameraInitialized = false;
      this.player.body.setTranslation(position, true);
      this.player.root.visible = true;
      this.player.collider.setCollisionGroups(PLAYER_GROUPS);
      this.player.collider.setEnabled(true);
      this.player.setControlMode("on-foot");
      return true;
    }
    return false;
  }

  #recordExitBlocked(): void {
    const car = this.#controlled;
    if (!car) return;
    const previous = this.#vehicleEvent;
    this.#vehicleEvent = {
      sequence: previous?.sequence ?? this.#eventSequence,
      damage: previous?.damage ?? 0,
      drivenHealth: car.health,
      otherCarId: previous?.otherCarId ?? 0,
      otherHealth: previous?.otherHealth ?? 0,
      drivenDisabled: car.health === 0,
      otherDisabled: previous?.otherDisabled ?? false,
      exitBlocked: true,
    };
  }

  #horizontalSpeed(velocity: RAPIER.Vector): number {
    return Math.hypot(velocity.x, velocity.z);
  }
}
